import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { of } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import { FtpType, PortalConfig, PortalCredential } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { ImmomioSelect } from '@immomio/ui/select';
import { ImmomioFormFieldComponent } from '@immomio/ui/form';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import { BasePortalCredentialControl } from '../../controls/base-portal-credential.control';
import { CredentialValidatorService } from '../../services/credential-validator.service';
import { FormFieldLabelComponent } from '../../../components/legacy/form/form-field/form-field-label/form-field-label.component';
import { AppInputDirective } from '../../../components/legacy/form/controls/input/input.directive';
import { FormFieldComponent } from '../../../components/legacy/form/form-field/form-field.component';
import { LoadingSpinnerComponent } from '../../../components/legacy/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-homepage-module-credential-details',
  templateUrl: './homepage-module-credential-details.component.html',
  styleUrls: ['./homepage-module-credential-details.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HomepageModuleCredentialDetailsComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormFieldLabelComponent,
    AppInputDirective,
    FormFieldComponent,
    ImmomioFormFieldComponent,
    ImmomioButtonComponent,
    ImmomioSelect,
    LoadingSpinnerComponent
  ]
})
export class HomepageModuleCredentialDetailsComponent
  extends BasePortalCredentialControl
  implements OnInit
{
  #destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private credentialValidatorService = inject(CredentialValidatorService);

  public ftpTypes = [
    { name: FtpType.FTP, value: FtpType.FTP },
    { name: FtpType.SFTP, value: FtpType.SFTP },
    { name: FtpType.FTPS, value: FtpType.FTPS }
  ];

  public readonly portalConfig = input<PortalConfig>(undefined);
  public readonly credential = input<PortalCredential>(undefined);

  public showFtpSettings = false;
  public checkingFtp = false;
  public validFtpConnection = false;
  public invalidFtpConnection = false;

  public get hostControl() {
    return this.propertiesControl.get('host');
  }

  public get portControl() {
    return this.propertiesControl.get('port');
  }

  constructor() {
    super();

    this.form = this.fb.group({
      name: ['', Validators.required],
      properties: this.fb.group({
        username: ['', Validators.required],
        password: ['', Validators.required],
        host: [''],
        port: [''],
        type: [FtpType.FTP]
      })
    });
  }

  public ngOnInit() {
    const credentialValue = this.credential();
    if (credentialValue) {
      this.form.patchValue(credentialValue);
    }

    this.form.valueChanges.subscribe(credential => {
      this.value = { ...credential };

      const hasFtpSetting =
        this.value.properties.host || this.value.properties.port;

      if (!this.showFtpSettings || !hasFtpSetting) {
        this.value.properties = {
          username: credential.properties.username,
          password: credential.properties.password
        };
      }

      this.onChange(this.value);
      this.onTouch();
    });

    this.propertiesControl.valueChanges.subscribe(() => {
      this.validFtpConnection = false;
      this.invalidFtpConnection = false;
    });

    this.credentialValidatorService.validationError$
      .pipe(
        filter(portal => !!portal),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(portal =>
        this.setCredentialValidationError(
          this.portalConfig().validationControl || 'password',
          portal === this.credential().portal
        )
      );
  }

  public toggleFtpSettings() {
    this.showFtpSettings = !this.showFtpSettings;

    if (this.showFtpSettings) {
      this.hostControl.setValidators(Validators.required);
      this.portControl.setValidators(Validators.required);
    } else {
      this.hostControl.clearValidators();
      this.portControl.clearValidators();
    }

    this.form.updateValueAndValidity();
  }

  public checkFtpConnection() {
    this.checkingFtp = true;
    this.invalidFtpConnection = false;

    this.credentialValidatorService
      .validateFtpConnection(this.credential())
      .pipe(
        catchError(() => {
          this.validFtpConnection = false;
          this.checkingFtp = false;
          this.invalidFtpConnection = true;

          this.cdr.detectChanges();

          return of(false);
        })
      )
      .subscribe(valid => {
        this.validFtpConnection = valid;
        this.invalidFtpConnection = !valid;
        this.checkingFtp = false;

        this.cdr.detectChanges();
      });
  }
}
